import { useState } from 'react'
import { useNavigate } from 'react-router'
import { EyeOff, Plus } from 'lucide-react'
import {
  EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useEngagementMutations, useSurveys } from '../api'
import { ANONYMITY_LABELS, SURVEY_KIND_LABELS, SURVEY_STATUS_LABELS } from '../types'
import type { SurveyAnonymity, SurveyKind, SurveyStatus } from '../types'

const STATUS_TONE: Record<SurveyStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  OPEN: 'info',
  CLOSED: 'success',
  CANCELLED: 'neutral',
}

/** Surveys and pulses: what was asked, of whom, and under what promise. */
export function SurveysPage() {
  useBreadcrumb()
  const navigate = useNavigate()
  const surveys = useSurveys()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { saveSurvey, publishSurvey, closeSurvey } = useEngagementMutations()

  const [draft, setDraft] = useState<{
    code: string; title: string; description: string; kind: SurveyKind
    anonymity: SurveyAnonymity; minGroupSize: string; closesOn: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Surveys"
        subtitle="What was asked, of whom, and under what promise about who sees the answers."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                code: '', title: '', description: '', kind: 'PULSE', anonymity: 'ANONYMOUS',
                minGroupSize: '', closesOn: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New survey
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <EyeOff className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        An anonymous survey's answers are stored in a table with no employee, no user and no
        "created by" column. Who answered is known, so people can be reminded; who said what
        is not, and cannot be worked out from this system. That promise is fixed the moment
        the survey goes out.
      </p>

      <Card>
        {surveys.isPending && <Skeleton className="m-4 h-24" />}
        {surveys.isError && (
          <ErrorState error={surveys.error} onRetry={() => void surveys.refetch()} />
        )}
        {surveys.isSuccess && surveys.data.length === 0 && (
          <EmptyState
            title="No survey has been run"
            description="A survey carries a promise about anonymity and a minimum group size below which nothing is shown. Neither has a default."
          />
        )}
        {surveys.isSuccess && surveys.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Survey</th>
                  <th className="p-3 text-start font-medium">Kind</th>
                  <th className="p-3 text-start font-medium">Answers seen by</th>
                  <th className="p-3 text-end font-medium">Asked</th>
                  <th className="p-3 text-end font-medium">Answered</th>
                  <th className="p-3 text-end font-medium">Smallest group shown</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {surveys.data.map((survey) => (
                  <tr key={survey.id} className="border-b border-slate-100 align-top">
                    <td className="p-3">
                      <button
                        type="button"
                        className="underline underline-offset-2"
                        onClick={() => { void navigate(`/engagement/surveys/${survey.id}`) }}
                      >
                        {survey.title}
                      </button>
                      <div className="font-mono text-xs text-slate-500">{survey.code}</div>
                    </td>
                    <td className="p-3">
                      {SURVEY_KIND_LABELS[survey.kind as SurveyKind] ?? survey.kind}
                    </td>
                    <td className="p-3 max-w-xs text-xs text-slate-600">
                      <span className="font-medium text-[var(--color-heading)]">
                        {ANONYMITY_LABELS[survey.anonymity as SurveyAnonymity]}
                      </span>
                      <div>{survey.anonymityNote}</div>
                    </td>
                    <td className="p-3 text-end">{survey.invited}</td>
                    <td className="p-3 text-end">{survey.responded}</td>
                    <td className="p-3 text-end">
                      {survey.minGroupSize ?? <span className="text-slate-500">Not set</span>}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={survey.status}
                        tone={STATUS_TONE[survey.status as SurveyStatus] ?? 'neutral'}
                        label={SURVEY_STATUS_LABELS[survey.status as SurveyStatus] ?? survey.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <div className="flex justify-end gap-2">
                        {survey.status === 'DRAFT' && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => act(
                              () => publishSurvey.mutateAsync(survey.id),
                              'That survey could not be published')}
                          >
                            Send it
                          </Button>
                        )}
                        {survey.status === 'OPEN' && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => act(
                              () => closeSurvey.mutateAsync(survey.id),
                              'That survey could not be closed')}
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        size="lg"
        title="New survey"
        description="Both choices here are promises to the people you are about to ask."
        submitLabel="Save survey"
        loading={saveSurvey.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft) return
          act(async () => {
            await saveSurvey.mutateAsync({
              body: {
                legalEntityId: entities.data?.[0]?.id ?? null,
                code: draft.code,
                title: draft.title,
                description: draft.description || null,
                kind: draft.kind,
                anonymity: draft.anonymity,
                audienceKind: 'ALL',
                minGroupSize: draft.minGroupSize ? Number(draft.minGroupSize) : null,
                closesOn: draft.closesOn || null,
              },
            })
            setDraft(null)
          }, 'That survey could not be saved', true)
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="survey-code">Code</Label>
              <Input
                id="survey-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="survey-kind">Kind</Label>
              <Select
                id="survey-kind"
                className="w-full"
                value={draft.kind}
                onChange={(event) =>
                  setDraft({ ...draft, kind: event.target.value as SurveyKind })}
              >
                {Object.entries(SURVEY_KIND_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="survey-title">Title</Label>
              <Input
                id="survey-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="survey-description">What it is for</Label>
              <Input
                id="survey-description" value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="survey-anonymity">Who sees the answers</Label>
              <Select
                id="survey-anonymity"
                className="w-full"
                value={draft.anonymity}
                onChange={(event) =>
                  setDraft({ ...draft, anonymity: event.target.value as SurveyAnonymity })}
              >
                <option value="ANONYMOUS">Nobody can tell who said what</option>
                <option value="IDENTIFIED">Answers carry the person's name</option>
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Fixed once it goes out. Anonymous answers are stored with no person on the row
                at all — not hidden, absent.
              </p>
            </div>
            <div>
              <Label htmlFor="survey-min-group">Smallest group shown</Label>
              <Input
                id="survey-min-group" type="number" value={draft.minGroupSize}
                onChange={(event) => setDraft({ ...draft, minGroupSize: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                No default. Below this many answers nothing is shown, because an "average" of
                three people is three people. Five is a common floor; your teams and what you
                have told them decide.
              </p>
            </div>
            <div>
              <Label htmlFor="survey-closes">Closes on</Label>
              <DatePicker
                id="survey-closes" value={draft.closesOn}
                onChange={(event) => setDraft({ ...draft, closesOn: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
